/**
 * Builds an AI-crawler-optimized FAQPage JSON-LD schema.
 *
 * Adds signals that ChatGPT / Perplexity / Gemini / Google AI Overviews
 * use to attribute, cite and trust Q&A content:
 *   - Stable @id on the FAQPage and each Question (page#faq, page#faq-N)
 *   - inLanguage on the page, every Question and every Answer
 *   - isPartOf → WebPage @id (entity linking)
 *   - about → Organization (TruWe) for topical authority
 *   - publisher with logo
 *   - dateModified / datePublished
 *   - speakable on the first three questions (voice/AI assistant pickup)
 *   - per-Question url (anchor) so AIs can deep-link a single answer
 *   - per-Answer author (TruWe Clinical Library) for E-E-A-T attribution
 *   - answerCount: 1 and upvoteCount: 0 (richer Question shape)
 */

const ORG_ID = "https://truwe.in/#organization";
const ORG = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "TruWe",
  url: "https://truwe.in",
  logo: { "@type": "ImageObject", url: "https://truwe.in/favicon.ico" },
};
const AUTHOR = {
  "@type": "Organization",
  name: "TruWe Clinical Library",
  url: "https://truwe.in",
};

const DEFAULT_DATE = "2026-06-08";

export type FaqItem = { q: string; a: string };

export interface BuildFaqSchemaArgs {
  /** Absolute canonical URL of the page hosting the FAQ. */
  pageUrl: string;
  faqs: FaqItem[];
  inLanguage?: string;
  datePublished?: string;
  dateModified?: string;
}

export function buildFaqPageSchema({
  pageUrl,
  faqs,
  inLanguage = "en-IN",
  datePublished = DEFAULT_DATE,
  dateModified = DEFAULT_DATE,
}: BuildFaqSchemaArgs) {
  const speakableXPath = [
    "/html/head/title",
    "//*[@itemprop='name']",
    "//*[contains(@class,'faq-question')]",
    "//*[contains(@class,'faq-answer')]",
  ];

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    url: pageUrl,
    inLanguage,
    datePublished,
    dateModified,
    isPartOf: { "@type": "WebPage", "@id": pageUrl },
    about: ORG,
    publisher: ORG,
    speakable: {
      "@type": "SpeakableSpecification",
      xpath: speakableXPath,
    },
    mainEntity: faqs.map((f, i) => {
      const qid = `${pageUrl}#faq-${i + 1}`;
      return {
        "@type": "Question",
        "@id": qid,
        url: qid,
        name: f.q,
        inLanguage,
        answerCount: 1,
        dateCreated: datePublished,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.a,
          inLanguage,
          url: qid,
          dateCreated: datePublished,
          upvoteCount: 0,
          author: AUTHOR,
        },
      };
    }),
  };
}
